import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/Iconify';
import { api } from 'src/Api';
import Alert from '@mui/material/Alert';
import { postJson } from 'src/utils/clutchApi';
import { RESET_EMAIL_KEY, RESET_TOKEN_KEY } from 'src/utils/clutchAdminAuth';

export default function ChangePasswordForm() {
  const navigate = useNavigate();
  const [alert, setalert] = useState({ visible: false, message: '', severity: 'error' });
  const [loading, setloading] = useState(false);
  const [showPassword, setshowPassword] = useState(false);
  const resetToken = sessionStorage.getItem(RESET_TOKEN_KEY);

  useEffect(() => {
    if (!resetToken) {
      navigate('/forgot', { replace: true });
    }
  }, [navigate, resetToken]);

  const RegisterSchema = Yup.object().shape({
    password: Yup.string().min(8, 'Password must be at least 8 characters').required('New password is required'),
    confirmPassword: Yup.string()
      .required('Confirm new password is required')
      .oneOf([Yup.ref('password')], 'The new password and confirm password do not match.')
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: () => {
      setPassword();
    }
  });

  const { errors, values, touched, handleSubmit, getFieldProps } = formik;

  async function setPassword() {
    setloading(true);
    setalert({ visible: false, message: '', severity: 'error' });

    try {
      const result = await postJson(api.passwordReset, {
        reset_token: resetToken,
        password: values.password,
        password_confirmation: values.confirmPassword
      });

      sessionStorage.removeItem(RESET_TOKEN_KEY);
      localStorage.removeItem(RESET_EMAIL_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
      localStorage.removeItem('adminPermissionSlugs');

      setalert({ visible: true, message: result.message, severity: 'success' });
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (error) {
      setalert({ visible: true, message: error.message, severity: 'error' });
    } finally {
      setloading(false);
    }
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="New password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setshowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Confirm new password"
            {...getFieldProps('confirmPassword')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setshowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
          />

          {alert.visible && (
            <Alert
              onClose={() => {
                setalert({ visible: false, message: '', severity: 'error' });
              }}
              severity={alert.severity}
            >
              {alert.message}
            </Alert>
          )}

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={loading}>
            Save password
          </LoadingButton>

          <LoadingButton
            fullWidth
            size="large"
            variant="outlined"
            onClick={() => {
              navigate('/login', { replace: true });
            }}
          >
            Back to login
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
